import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import store, { persistor } from "./Redux/store";
import { Provider, useSelector, useDispatch } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import { generateToken, messaging } from "./Firebase/firebase";
import { onMessage } from "firebase/messaging";
import toast from "react-hot-toast";
import { sendFirebaseToken, logout } from "./Redux/Actions/authActions";
import { getEnums } from "./Redux/Actions/enumsActions";

if ("serviceWorker" in navigator) {
  navigator.serviceWorker
    .register("/firebase-messaging-sw.js")
    .then(function (registration) {
      console.log("Service Worker registered with scope:", registration.scope);
    })
    .catch(function (error) {
      console.error("Service Worker registration failed:", error);
    });
}

const loadGoogleMaps = (callback) => {
  if (window.google && window.google.maps) {
    callback();
    return;
  }

  const script = document.createElement("script");
  script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&libraries=places&libraries=geometry`;
  script.async = true;
  script.defer = true;
  script.onload = () => {
    console.log("✅ Google Maps API loaded successfully");
    callback();
  };
  script.onerror = () => {
    console.error("❌ Failed to load Google Maps API");
  };
  document.head.appendChild(script);
};

const AppInner = () => {
  const dispatch = useDispatch();
  const { admin, isAuthenticated } = useSelector((state) => state.admin || {});

  useEffect(() => {
    if (!isAuthenticated) return;

    const TWO_DAYS = Number(process.env.REACT_APP_DAY_OF_LOGOUT);
    const loginTime = Number(localStorage.getItem("loginTime"));
    const elapsed = Date.now() - loginTime;
    const remainingTime = TWO_DAYS - elapsed;

    if (remainingTime <= 0) {
      dispatch(logout());
      return;
    }

    const timer = setTimeout(() => {
      dispatch(logout());
      toast.error("Session expired");
      window.location.href = "/";
    }, remainingTime);

    return () => clearTimeout(timer);
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    const getTokenAndSave = async () => {
      const token = await generateToken();
      if (token) {
        dispatch(
          sendFirebaseToken({
            admin_id: admin?.data?._id,
            firebase_token: token,
          })
        );
        dispatch(getEnums());
      }
    };

    getTokenAndSave();
    onMessage(messaging, (payload) => {
      toast(payload.notification.body);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <App />;
};

const RootApp = () => {
  useEffect(() => {
    loadGoogleMaps(() => {
      window.googleMapsLoaded = true;
      console.log("🎉 Google Maps ready to use!");
    });
  }, []);

  return (
    <BrowserRouter>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <AppInner />
        </PersistGate>
      </Provider>
    </BrowserRouter>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<RootApp />);